import React from "react";
import { Trophy, Gauge, Zap, Focus, Heart, Repeat } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const Performance = () => (
  <ApplicationTemplate
    pageKey="performance"
    breadcrumbLabel="Performance"
    overline="Application · Performance"
    headline="The ceiling of performance"
    headlineAccent="is set by autonomic capacity."
    lead="Elite output is not the absence of stress — it is the physiological capacity to receive stress, adapt, and return. Adaptive Physiology treats performance as the visible expression of a well-regulated nervous system."
    loopHighlight="feedback"
    challenge={{
      title: "Most performers train load. Few train adaptation quality.",
      paragraphs: [
        "Training volume and intensity are optimized aggressively. What often is not — is the physiological substrate that determines whether the body actually adapts to that training, and whether the mind is regulated enough to execute it under pressure.",
        "Overreach without adaptive recovery narrows HRV, blunts sleep, and quietly builds a ceiling the athlete can’t see. Performance stalls, even as work volume rises.",
      ],
    }}
    traditionalGap={{
      title: "Performance frameworks often stop at load and biomechanics.",
      paragraphs: [
        "Programming, mobility, nutrition, and sport-specific skill are handled with real sophistication. Autonomic regulation, sleep architecture, and adaptive-recovery cadence often are not.",
        "Adaptive Physiology is not opposed to any performance system. It sits underneath — the physiological quality that decides whether everything above it actually converts into output.",
      ],
    }}
    perspective={{
      title: "Regulate the system that has to execute.",
      paragraphs: [
        "HRV-guided training, respiratory pacing, structured recovery, and sleep architecture are used to widen the autonomic band the athlete performs inside. Higher HRV headroom means more available capacity — under load, on demand.",
        "The best performers already know: the edge is not more work. The edge is a nervous system that adapts faster than the competition’s.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline the autonomic profile",
        body: "HRV, resting HR, sleep, subjective readiness, and current load are captured together — the true starting signal for the loop.",
      },
      {
        title: "Prescribe adaptive stimulus, not just load",
        body: "Training, breathing, and recovery are dosed to produce adaptation — not to accumulate volume.",
      },
      {
        title: "Reinforce with disciplined rhythm",
        body: "Recovery inputs are protected with the same rigor as training inputs. Cadence is where the ceiling actually rises.",
      },
      {
        title: "Retrain the readiness default",
        body: "The system’s baseline moves. What used to be a peak becomes an ordinary day. That is what a raised ceiling actually is.",
      },
    ]}
    capacities={[
      { icon: Gauge, label: "HRV headroom", body: "More autonomic room for stress, output, and adaptation." },
      { icon: Heart, label: "Recovery velocity", body: "Faster return to baseline after peak load." },
      { icon: Focus, label: "Executable focus", body: "A regulated system that can hold attention where it matters." },
      { icon: Zap, label: "Adaptive capacity", body: "The physiological ability to convert training into progress." },
      { icon: Repeat, label: "Consistency under load", body: "Repeated output without accumulating cost." },
      { icon: Trophy, label: "Ceiling expansion", body: "A raised set-point — not a temporary peak." },
    ]}
  />
);
export default Performance;
